/**
 * Change tracking (LyX \change_inserted / \change_deleted):
 *  - when tracking is on, typed text gets an "inserted" change mark for the current author;
 *  - Backspace/Delete over unchanged text marks it "deleted" instead of removing it;
 *  - accept/reject all changes.
 */
import { Plugin, PluginKey, TextSelection, type Command, type Transaction, type EditorState } from 'prosemirror-state';
import { Decoration, DecorationSet, type EditorView } from 'prosemirror-view';
import { Fragment, Slice, type Mark, type Node as PMNode } from 'prosemirror-model';
import { ReplaceStep } from 'prosemirror-transform';
import { schema, changeDomAttrs } from '@overlyx/core';
import { editorContext } from '../context';

export const changesKey = new PluginKey('lyx-changes');

function now(): number { return Math.floor(Date.now() / 1000); }

function changeMark(type: 'inserted' | 'deleted') {
  return schema.marks.change.create({ type, author: editorContext.changeAuthorId ?? 0, time: now() });
}

/**
 * The author's own insertion right before `pos`: typing on extends it (one \lyxadded group) —
 * the change mark is not inclusive, and LyX's Change::isSimilarTo ignores the time as well.
 */
function ownInsertionBefore(doc: PMNode, pos: number): Mark | null {
  const before = doc.resolve(pos).nodeBefore;
  const m = before?.isText ? before.marks.find(x => x.type === schema.marks.change) : undefined;
  return m && m.attrs.type === 'inserted' && m.attrs.author === (editorContext.changeAuthorId ?? 0) ? m : null;
}

/** Inline nodes that never carry a tracked change of their own: a margin drawing's anchor is not text. */
const UNTRACKED_NODES = new Set(['sketch']);

/**
 * Comment threads are no part of the text: starting one, writing in it or deleting it is never a
 * suggestion (as in Google Docs) — a comment made while suggesting came wrapped in a tracked
 * insertion, which a reviewer then had to accept before the thread was an ordinary one.
 */
const isComment = (n: PMNode) => n.type.name === 'inset' && n.attrs.name === 'Note' && n.attrs.arg === 'Comment';
function inComment(doc: PMNode, pos: number): boolean {
  const $p = doc.resolve(pos);
  for (let d = $p.depth; d > 0; d--) if (isComment($p.node(d))) return true;
  return false;
}

/**
 * Adds the inserted-mark to what the user inserts while tracking is active: text gets the change
 * mark, an inline node (a pasted formula, a reference, a footnote…) records it in its `marks`
 * attribute — like LyX, which marks the inset's position. Only pure insertions mark nodes: a
 * node replaced by a new version of itself (a formula being edited, an inset opened or closed)
 * stays as it was — LyX does not track edits inside math either.
 */
function withChange(node: PMNode, attrs: Record<string, unknown>): Record<string, unknown> {
  const marks = JSON.parse(node.attrs.marks || '[]').filter((m: any) => m.type !== 'change');
  marks.push({ type: 'change', attrs });
  return { ...node.attrs, marks: JSON.stringify(marks) };
}

/** Keep removed original content in the document, including paragraph boundaries. */
function deletedSlice(slice: Slice): Slice {
  const del = changeMark('deleted');
  const walk = (content: Fragment, openEnd: number): Fragment => {
    const nodes: PMNode[] = [];
    let joinNext = false;
    content.forEach((node, _offset, index) => {
      const end = index === content.childCount - 1 ? openEnd : 0;
      const ch = changeOf(node);
      if (node.isInline) {
        if (UNTRACKED_NODES.has(node.type.name) || isComment(node) || (ch?.type === 'inserted' && ch.author === editorContext.changeAuthorId)) return;
        if (ch?.type === 'deleted') nodes.push(node);
        else nodes.push(node.isText ? node.mark(del.addToSet(node.marks)) : node.type.create(withChange(node, del.attrs), node.content, node.marks));
      } else {
        const boundary = node.attrs.endChange ? JSON.parse(node.attrs.endChange) : null;
        const cancelBoundary = node.type.name === 'paragraph' && end === 0 && boundary?.type === 'inserted' && boundary.author === editorContext.changeAuthorId;
        const attrs = node.type.name === 'paragraph' && end === 0 ? { ...node.attrs, endChange: cancelBoundary ? null : JSON.stringify(del.attrs) } : node.attrs;
        const rebuilt = node.type.create(attrs, walk(node.content, Math.max(0, end - 1)), node.marks);
        if (joinNext && rebuilt.type.name === 'paragraph' && nodes.at(-1)?.type === rebuilt.type) {
          const previous = nodes.pop()!;
          nodes.push(previous.type.create({ ...previous.attrs, endChange: rebuilt.attrs.endChange }, previous.content.append(rebuilt.content), previous.marks));
        } else nodes.push(rebuilt);
        joinNext = cancelBoundary;
      }
    });
    return Fragment.from(nodes);
  };
  return new Slice(walk(slice.content, slice.openEnd), slice.openStart, slice.openEnd);
}

/**
 * Said once per session, the first time a formula that was there before is edited while changes are
 * tracked: the edit is applied as it is, not as a suggestion — LyX does not track inside math either
 * (a formula is one value). A formula inserted as a suggestion is one, edits included.
 */
let formulaNoticeShown = false;
function noticeUntrackedFormula(before: PMNode, after: PMNode): void {
  if (formulaNoticeShown || !/^math_/.test(before.type.name) || before.attrs.latex === after.attrs.latex) return;
  if (changeOf(before)?.type === 'inserted') return;
  formulaNoticeShown = true;
  editorContext.notify?.('Suggesting: edits inside an existing formula are applied directly, not tracked (as in LyX) — to suggest a different formula, insert the new one beside it and delete the old one');
}
/** tests: show the notice again */
export function resetFormulaNotice(): void { formulaNoticeShown = false; }

export function changeTrackingPlugin(): Plugin {
  return new Plugin({
    key: changesKey,
    appendTransaction(trs, _old, newState) {
      if (!editorContext.trackChanges || editorContext.changeAuthorId === undefined) return null;
      const tr = newState.tr;
      for (let ti = 0; ti < trs.length; ti++) {
        const t = trs[ti];
        if (!t.docChanged || t.getMeta('lyx-changes') || t.getMeta('y-sync$') || t.getMeta('addToHistory') === false) continue;
        const structural: { from: number; to: number }[] = [];
        const structure = (n: PMNode) => JSON.stringify([n.attrs.columns, n.content.content.map(row => row.content.content.map(cell => [cell.attrs.colspan, cell.attrs.rowspan]))]);
        t.before.descendants((old, pos) => {
          if (old.type.name !== 'table') return true;
          const next = t.doc.nodeAt(t.mapping.map(pos, 1));
          if (!next || next.type !== old.type || structure(next) === structure(old)) return false;
          structural.push({ from: pos, to: pos + old.nodeSize });
          let at = t.mapping.map(pos, 1);
          for (let j = ti + 1; j < trs.length; j++) at = trs[j].mapping.map(at, 1);
          at = tr.mapping.map(at, 1);
          const current = tr.doc.nodeAt(at);
          if (current?.type !== old.type) return false;
          // A structural table edit is one reviewable replacement. Keep the complete
          // original grid (including spans and cell formatting) so rejection is lossless.
          tr.setNodeMarkup(at, undefined, withChange(current, changeMark('inserted').attrs));
          if (!(changeOf(old)?.type === 'inserted' && changeOf(old)?.author === editorContext.changeAuthorId)) {
            tr.insert(at, old.type.create(withChange(old, changeMark('deleted').attrs), old.content));
          }
          return false;
        });
        for (let i = 0; i < t.steps.length; i++) {
          const step = t.steps[i];
          if (!(step instanceof ReplaceStep)) continue;
          if (structural.some(r => step.from >= r.from && step.from <= r.to)) continue;
          if (inComment(t.docs[i], step.from) && inComment(t.docs[i], step.to)) continue;
          // Editing attributes of an existing formula is not a textual replacement.
          const oldNode = t.docs[i].nodeAt(step.from);
          if (step.to > step.from && oldNode && !oldNode.isText && oldNode.isAtom && step.to - step.from === oldNode.nodeSize && step.slice.content.childCount === 1 && step.slice.content.firstChild?.type === oldNode.type) {
            noticeUntrackedFormula(oldNode, step.slice.content.firstChild);
            continue;
          }
          const mapToCurrent = (pos: number, assoc: number) => {
            for (let j = i + 1; j < t.mapping.maps.length; j++) pos = t.mapping.maps[j].map(pos, assoc);
            for (let j = ti + 1; j < trs.length; j++) pos = trs[j].mapping.map(pos, assoc);
            return tr.mapping.map(pos, assoc);
          };
          step.getMap().forEach((os, oe, ns, ne) => {
            const from = mapToCurrent(ns, 1), to = mapToCurrent(ne, -1);
            if (to > from) {
              const ins = ownInsertionBefore(tr.doc, from) ?? changeMark('inserted');
              tr.doc.nodesBetween(from, to, (node, pos) => {
                if (isComment(node)) return false;
                if (node.isText) {
                  if (!changeOf(node)) tr.addMark(Math.max(from, pos), Math.min(to, pos + node.nodeSize), ins);
                } else if (node.isInline && pos >= from && pos + node.nodeSize <= to && !UNTRACKED_NODES.has(node.type.name)) {
                  if (!changeOf(node)) tr.setNodeMarkup(pos, undefined, withChange(node, ins.attrs));
                  return false;
                } else if (node.type.name === 'paragraph' && !node.attrs.endChange && pos + node.nodeSize - 1 >= from && pos + node.nodeSize - 1 < to) {
                  tr.setNodeMarkup(pos, undefined, { ...node.attrs, endChange: JSON.stringify(ins.attrs) });
                }
                return true;
              });
            }
            if (oe > os) {
              const original = t.docs[i].slice(os, oe);
              let removed = deletedSlice(original);
              let at = mapToCurrent(ns, -1);
              // Block paste expands a selection at the start of a paragraph to include
              // its opening token. Restore that partial paragraph inside the pasted
              // first paragraph, rather than inventing another paragraph boundary.
              if (!removed.openStart && removed.openEnd && removed.content.firstChild?.type.name === 'paragraph' && tr.doc.nodeAt(at)?.type.name === 'paragraph') {
                at++;
                removed = new Slice(removed.content, 1, removed.openEnd);
              }
              if (to > from && removed.openEnd === 1 && removed.content.childCount > 1 && removed.content.lastChild?.type.name === 'paragraph') {
                const destination = tr.doc.resolve(at).parent;
                if (destination.type.name === 'paragraph') {
                  const last = removed.content.lastChild;
                  const tail = last.type.create({ ...last.attrs, endChange: destination.attrs.endChange }, last.content, last.marks);
                  removed = new Slice(removed.content.replaceChild(removed.content.childCount - 1, tail), removed.openStart, removed.openEnd);
                }
              }
              if (removed.size) {
                tr.replace(at, at, removed);
              }
              // Fitting an open slice keeps the destination paragraph's attributes.
              // Restore its boundary marker, including cancellation of a new break.
              if (removed.openStart && original.content.childCount > 1 && removed.content.firstChild?.type.name === 'paragraph') {
                const $at = tr.doc.resolve(at);
                if ($at.parent.type.name === 'paragraph') tr.setNodeMarkup($at.before(), undefined, { ...$at.parent.attrs, endChange: removed.content.firstChild.attrs.endChange });
              }
            }
          });
        }
      }
      if (tr.docChanged) return tr.setMeta('lyx-changes', true);
      return null;
    },
    props: {
      decorations(state) {
        const decos: Decoration[] = [];
        const filter = changesFilterKey.getState(state);
        state.doc.descendants((node, pos) => {
          if (node.type.name !== 'paragraph' || !node.attrs.endChange) return true;
          const change = JSON.parse(node.attrs.endChange);
          if (change.type === 'inserted' ? filter?.showInsertions === false : filter?.showDeletions === false) return true;
          const at = pos + node.nodeSize - 1;
          decos.push(Decoration.widget(at, view => {
            const el = document.createElement('span');
            for (const [name, value] of Object.entries(changeDomAttrs(change))) el.setAttribute(name, value);
            el.classList.add('lyx-change-boundary');
            el.textContent = '¶';
            el.title = `${change.type === 'inserted' ? 'Inserted' : 'Deleted'} paragraph break`;
            el.setAttribute('aria-label', el.title);
            el.onmousedown = event => {
              event.preventDefault();
              view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, at, Math.min(at + 2, view.state.doc.content.size))).setMeta('addToHistory', false));
              view.focus();
            };
            return el;
          }, { side: 1 }));
          return true;
        });
        return decos.length ? DecorationSet.create(state.doc, decos) : null;
      },
    },
  });
}

/**
 * Show a node's tracked change (kept in its `marks` attribute, see the schema) on the node
 * view's DOM the way the change mark shows on text: the `lyx-change-*` classes colour the
 * formula / reference / graphic in the author's colour, `data-author` picks that colour and the
 * hover tooltip reads the attributes. Node views call this whenever they (re)render their DOM.
 */
export function applyChangeAttrs(dom: HTMLElement, node: PMNode): void {
  applyNodeFont(dom, node);
  const c = changeOf(node);
  if (!c) {
    if (dom.dataset.changed === undefined) return;
    dom.classList.remove('lyx-change', 'lyx-change-inserted', 'lyx-change-deleted');
    delete dom.dataset.changed; delete dom.dataset.author; delete dom.dataset.time;
    return;
  }
  const attrs = changeDomAttrs(c);
  dom.classList.toggle('lyx-change', true);
  dom.classList.toggle('lyx-change-inserted', c.type === 'inserted');
  dom.classList.toggle('lyx-change-deleted', c.type === 'deleted');
  for (const [k, v] of Object.entries(attrs)) if (k !== 'class' && dom.getAttribute(k) !== v) dom.setAttribute(k, v);
}

/**
 * The font an inline node (a formula) carries in its `marks` attr, drawn on its element: the size
 * (a named LaTeX size, or points — `--ol-pt` is a point at the current zoom) and the colour.
 */
export function applyNodeFont(dom: HTMLElement, node: PMNode): void {
  let list: { type: string; attrs?: { value?: string } }[] = [];
  try { list = JSON.parse(node.attrs.marks || '[]'); } catch { /* none */ }
  const size = list.find(m => m.type === 'size')?.attrs?.value;
  const color = list.find(m => m.type === 'color')?.attrs?.value;
  const prevSize = dom.dataset.size;
  if (prevSize && prevSize !== size) dom.classList.remove('lyx-size-' + prevSize);
  if (size) {
    dom.dataset.size = size;
    const abs = /^(\d+(?:\.\d+)?)pt/.exec(size);
    if (abs) dom.style.fontSize = `calc(var(--ol-pt) * ${abs[1]} * var(--ol-text-scale, 1))`;
    else { dom.classList.add('lyx-size-' + size); dom.style.fontSize = ''; }
  } else if (prevSize) { delete dom.dataset.size; dom.style.fontSize = ''; }
  // the colour as coloured text has it: LyX's named colours by their class (styles.css, dark mode too), a picked one as it is
  const prevColor = dom.dataset.color;
  if (prevColor && prevColor !== color) dom.classList.remove('lyx-color-' + prevColor);
  if (color) { dom.dataset.color = color; if (color.startsWith('#')) dom.style.color = color; else { dom.classList.add('lyx-color-' + color); dom.style.color = ''; } }
  else if (prevColor) { delete dom.dataset.color; dom.style.color = ''; }
}

/** Backspace/Delete while tracking: mark as deleted (unless the text was inserted by tracking, then remove). */
export function trackedDelete(dir: -1 | 1): Command {
  return (state, dispatch) => {
    if (!editorContext.trackChanges || editorContext.changeAuthorId === undefined) return false;
    const sel = state.selection;
    // Comments are edited directly, including keyboard deletions (as in appendTransaction).
    if (inComment(state.doc, sel.from) && inComment(state.doc, sel.to)) return false;
    if (!sel.empty && !sel.$from.sameParent(sel.$to)) return false;
    let from: number, to: number;
    if (!sel.empty) { from = sel.from; to = sel.to; }
    else {
      const $c = sel.$from;
      if (dir < 0) { if ($c.parentOffset === 0) return false; from = $c.pos - 1; to = $c.pos; }
      else { if ($c.parentOffset === $c.parent.content.size) return false; from = $c.pos; to = $c.pos + 1; }
      // step over a whole inline node
      const n = dir < 0 ? $c.nodeBefore : $c.nodeAfter;
      if (n && !n.isText) { if (dir < 0) from = $c.pos - n.nodeSize; else to = $c.pos + n.nodeSize; }
    }
    if (!dispatch) return true;
    let tr = state.tr;
    // Text inserted by change tracking is simply removed; other text is marked deleted.
    const del = changeMark('deleted');
    const removals: [number, number][] = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (!node.isInline) return true;
      const s = Math.max(from, pos), e = Math.min(to, pos + node.nodeSize);
      if (isComment(node)) { removals.push([s, e]); return false; }
      const ch = node.marks.find(m => m.type === schema.marks.change);
      if (ch && ch.attrs.type === 'inserted') removals.push([s, e]);
      else if (!(ch && ch.attrs.type === 'deleted')) {
        if (node.isText) tr = tr.addMark(s, e, del);
        else {
          const marks = JSON.parse(node.attrs.marks || '[]').filter((m: any) => m.type !== 'change');
          marks.push({ type: 'change', attrs: del.attrs });
          tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, marks: JSON.stringify(marks) });
        }
      }
      return false;
    });
    for (const [s, e] of removals.reverse()) tr = tr.delete(s, e);
    tr = tr.setSelection(TextSelection.create(tr.doc, dir < 0 ? tr.mapping.map(from) : tr.mapping.map(to)));
    dispatch(tr.setMeta('lyx-changes', true));
    return true;
  };
}

export function acceptAllChanges(): Command {
  return (state, dispatch) => {
    let tr = state.tr;
    const deletions: [number, number][] = [];
    const unmark: [number, number][] = [];
    const nodeFix: [number, PMNode][] = [];
    const joins: number[] = [];
    state.doc.descendants((node, pos) => {
      if (node.isText) {
        const ch = node.marks.find(m => m.type === schema.marks.change);
        if (ch?.attrs.type === 'deleted') { deletions.push([pos, pos + node.nodeSize]); return false; }
        else if (ch) unmark.push([pos, pos + node.nodeSize]);
      } else if (node.isInline) {
        const marks: any[] = JSON.parse(node.attrs.marks || '[]');
        const ch = marks.find(m => m.type === 'change');
        if (ch?.attrs.type === 'deleted') { deletions.push([pos, pos + node.nodeSize]); return false; }
        else if (ch) nodeFix.push([pos, node]);
      } else if (node.type.name === 'paragraph' && node.attrs.endChange) {
        nodeFix.push([pos, node]);
        const ch = JSON.parse(node.attrs.endChange);
        if (ch.type === 'deleted') joins.push(pos + node.nodeSize);
      }
      return true;
    });
    for (const [s, e] of unmark) tr = tr.removeMark(s, e, schema.marks.change);
    for (const [pos, node] of nodeFix) {
      if (node.type.name === 'paragraph') tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, endChange: null });
      else tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, marks: JSON.stringify(JSON.parse(node.attrs.marks || '[]').filter((m: any) => m.type !== 'change')) });
    }
    for (const [s, e] of deletions.sort((a, b) => b[0] - a[0])) tr = tr.delete(tr.mapping.map(s), tr.mapping.map(e));
    for (const pos of joins.sort((a, b) => b - a)) {
      const at = tr.mapping.map(pos, -1);
      if (at > 0 && at < tr.doc.content.size && tr.doc.resolve(at).nodeBefore?.type === schema.nodes.paragraph && tr.doc.resolve(at).nodeAfter?.type === schema.nodes.paragraph) tr.join(at);
    }
    if (!tr.docChanged) return false;
    dispatch?.(tr.setMeta('lyx-changes', true));
    return true;
  };
}

export function rejectAllChanges(): Command {
  return (state, dispatch) => {
    let tr = state.tr;
    const deletions: [number, number][] = [];
    const unmark: [number, number][] = [];
    const nodeFix: [number, PMNode][] = [];
    const joins: number[] = [];
    state.doc.descendants((node, pos) => {
      if (node.isText) {
        const ch = node.marks.find(m => m.type === schema.marks.change);
        if (ch?.attrs.type === 'inserted') { deletions.push([pos, pos + node.nodeSize]); return false; }
        else if (ch) unmark.push([pos, pos + node.nodeSize]);
      } else if (node.isInline) {
        const marks: any[] = JSON.parse(node.attrs.marks || '[]');
        const ch = marks.find(m => m.type === 'change');
        if (ch?.attrs.type === 'inserted') { deletions.push([pos, pos + node.nodeSize]); return false; }
        else if (ch) nodeFix.push([pos, node]);
      } else if (node.type.name === 'paragraph' && node.attrs.endChange) {
        nodeFix.push([pos, node]);
        if (JSON.parse(node.attrs.endChange).type === 'inserted') joins.push(pos + node.nodeSize);
      }
      return true;
    });
    for (const [s, e] of unmark) tr = tr.removeMark(s, e, schema.marks.change);
    for (const [pos, node] of nodeFix) {
      if (node.type.name === 'paragraph') tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, endChange: null });
      else tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, marks: JSON.stringify(JSON.parse(node.attrs.marks || '[]').filter((m: any) => m.type !== 'change')) });
    }
    for (const [s, e] of deletions.sort((a, b) => b[0] - a[0])) tr = tr.delete(tr.mapping.map(s), tr.mapping.map(e));
    for (const pos of joins.sort((a, b) => b - a)) {
      const at = tr.mapping.map(pos, -1);
      if (at > 0 && at < tr.doc.content.size && tr.doc.resolve(at).nodeBefore?.type === schema.nodes.paragraph && tr.doc.resolve(at).nodeAfter?.type === schema.nodes.paragraph) tr.join(at);
    }
    if (!tr.docChanged) return false;
    dispatch?.(tr.setMeta('lyx-changes', true));
    return true;
  };
}

export function hasChanges(doc: PMNode): boolean {
  let found = false;
  doc.descendants((node) => {
    if (found) return false;
    if (node.type.name === 'paragraph' && node.attrs.endChange) found = true;
    else if (node.isText && node.marks.some(m => m.type === schema.marks.change)) found = true;
    else if (node.isInline && (node.attrs.marks || '').includes('"change"')) found = true;
    return !found;
  });
  return found;
}

/* ------------------------------------------------ single change at the cursor */

export interface ChangeRange { from: number; to: number; type: 'inserted' | 'deleted'; author: number; time: number; boundary?: boolean }

export function changeOf(node: PMNode): { type: 'inserted' | 'deleted'; author: number; time: number } | null {
  if (node.isText) {
    const m = node.marks.find(x => x.type === schema.marks.change);
    return m ? { type: m.attrs.type, author: Number(m.attrs.author), time: Number(m.attrs.time) } : null;
  }
  if (node.isInline) {
    try {
      const ch = JSON.parse(node.attrs.marks || '[]').find((x: any) => x.type === 'change');
      return ch ? { type: ch.attrs.type, author: Number(ch.attrs.author), time: Number(ch.attrs.time) } : null;
    } catch { return null; }
  }
  return null;
}

/** The tracked change (contiguous run with the same author/type) at a position, if any. */
export function changeAt(state: EditorState, pos: number): ChangeRange | null {
  const $p = state.doc.resolve(pos);
  const parent = $p.parent, base = $p.start();
  if (!parent.isTextblock) return null;
  // find the child at pos (prefer the one after the cursor, then before)
  let idx = $p.index();
  let child = parent.maybeChild(idx);
  let ch = child ? changeOf(child) : null;
  if (!ch && idx > 0) { idx--; child = parent.child(idx); ch = changeOf(child); }
  if (!ch || !child) {
    for (let depth = $p.depth; depth > 0; depth--) {
      const ancestor = $p.node(depth), change = changeOf(ancestor);
      if (change) return { from: $p.before(depth), to: $p.before(depth) + ancestor.nodeSize, ...change };
    }
    if (parent.attrs.endChange) {
      const change = JSON.parse(parent.attrs.endChange);
      return { from: base + parent.content.size, to: base + parent.content.size + 2, ...change, boundary: true };
    }
    return null;
  }
  const same = (n: PMNode) => { const c = changeOf(n); return !!c && c.type === ch!.type && c.author === ch!.author; };
  let a = idx, b = idx;
  while (a > 0 && same(parent.child(a - 1))) a--;
  while (b + 1 < parent.childCount && same(parent.child(b + 1))) b++;
  let from = base, to = base;
  for (let i = 0; i < a; i++) from += parent.child(i).nodeSize;
  to = from;
  for (let i = a; i <= b; i++) to += parent.child(i).nodeSize;
  return { from, to, ...ch };
}

/** Accept (keep insertions / drop deletions) or reject one change range. */
export function resolveChange(range: ChangeRange, accept: boolean): Command {
  return (state, dispatch) => {
    let tr = state.tr;
    const remove = accept ? range.type === 'deleted' : range.type === 'inserted';
    if (range.boundary) {
      resolveBoundary(tr, range, remove);
      if (!tr.docChanged) return false;
      dispatch?.(tr.setMeta('lyx-changes', true));
      return true;
    }
    if (remove) tr = tr.delete(range.from, range.to);
    else {
      tr = tr.removeMark(range.from, range.to, schema.marks.change);
      state.doc.nodesBetween(range.from, range.to, (node, pos) => {
        if (node.isInline && !node.isText && (node.attrs.marks || '').includes('"change"')) {
          tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, marks: JSON.stringify(JSON.parse(node.attrs.marks || '[]').filter((m: any) => m.type !== 'change')) });
        }
        return true;
      });
    }
    if (!tr.docChanged) return false;
    dispatch?.(tr.setMeta('lyx-changes', true));
    return true;
  };
}

/**
 * A change author's name from the document's author table. Somebody who started suggesting after
 * this editor loaded the table is not in it yet (their name showed as "author 83031974"): the table
 * is fetched again, once per such author, and meanwhile they are "a co-author".
 */
const authorsAsked = new Set<number>();
export function changeAuthorName(id: number): string {
  const name = editorContext.meta?.authors.find(a => a.id === id)?.name;
  if (name) return name;
  // one's own first change, before the table has it (the VS Code editor registers no author)
  if (id === editorContext.changeAuthorId && editorContext.user?.name) return editorContext.user.name;
  if (!authorsAsked.has(id)) { authorsAsked.add(id); editorContext.refreshAuthors?.(); }
  return 'a co-author';
}

/* ------------------------------------------------ navigation (LyX change-next / change-previous) */

/** All tracked change runs of the document in document order (adjacent same author/type merged). */
export function allChanges(doc: PMNode): ChangeRange[] {
  const out: ChangeRange[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === 'paragraph' && node.attrs.endChange) out.push({ from: pos + node.nodeSize - 1, to: pos + node.nodeSize + 1, ...JSON.parse(node.attrs.endChange), boundary: true });
    if (!node.isInline) return true;
    const c = changeOf(node);
    if (!c) return true;
    const last = out[out.length - 1];
    if (last && !last.boundary && last.to === pos && last.type === c.type && last.author === c.author) last.to = pos + node.nodeSize;
    else out.push({ from: pos, to: pos + node.nodeSize, ...c });
    return false;
  });
  return out.sort((a, b) => a.from - b.from);
}

function resolveBoundary(tr: Transaction, range: ChangeRange, remove: boolean): void {
  const $end = tr.doc.resolve(Math.min(range.from, tr.doc.content.size));
  if ($end.parent.type.name !== 'paragraph') return;
  tr.setNodeMarkup($end.before(), undefined, { ...$end.parent.attrs, endChange: null });
  const at = $end.after();
  if (remove && at < tr.doc.content.size && tr.doc.resolve(at).nodeAfter?.type.name === 'paragraph') tr.join(at);
}

/** Move the cursor to the next / previous tracked change (wraps around); selects it. */
export function gotoChange(dir: 1 | -1): Command {
  return (state, dispatch) => {
    const all = allChanges(state.doc);
    if (!all.length) return false;
    const { from, to } = state.selection;
    let target: ChangeRange | undefined;
    if (dir > 0) target = all.find(c => c.from >= to || (c.from > from && c.to > to)) ?? all[0];
    else { for (let i = all.length - 1; i >= 0; i--) if (all[i].to <= from || (all[i].from < from && all[i].to < to)) { target = all[i]; break; } target = target ?? all[all.length - 1]; }
    if (!target) return false;
    if (dispatch) {
      const tr = state.tr.setSelection(TextSelection.create(state.doc, target.from, target.to)).scrollIntoView();
      dispatch(tr.setMeta('addToHistory', false));
    }
    return true;
  };
}

/** Accept or reject every change touching the selection (LyX change-accept / change-reject); with an
 *  empty selection, the change under the cursor. */
export function resolveSelectionChanges(accept: boolean): Command {
  return (state, dispatch) => {
    const { from, to, empty } = state.selection;
    const ranges = empty ? [changeAt(state, from)].filter((c): c is ChangeRange => !!c) : allChanges(state.doc).filter(c => c.from < to && c.to > from);
    return resolveChanges(ranges, accept)(state, dispatch);
  };
}

/** Accept or reject the given change runs (positions in the state's document). */
export function resolveChanges(ranges: readonly ChangeRange[], accept: boolean): Command {
  return (state, dispatch) => {
    if (!ranges.length) return false;
    if (!dispatch) return true;
    let tr = state.tr;
    for (const r of [...ranges].sort((a, b) => b.from - a.from)) {
      const remove = accept ? r.type === 'deleted' : r.type === 'inserted';
      if (r.boundary) { resolveBoundary(tr, r, remove); continue; }
      if (remove) tr = tr.delete(r.from, r.to);
      else {
        tr = tr.removeMark(r.from, r.to, schema.marks.change);
        tr.doc.nodesBetween(r.from, r.to, (node, pos) => {
          if (node.isInline && !node.isText && (node.attrs.marks || '').includes('"change"')) tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, marks: JSON.stringify(JSON.parse(node.attrs.marks || '[]').filter((m: any) => m.type !== 'change')) });
          return true;
        });
      }
    }
    if (!tr.docChanged) return false;
    dispatch(tr.setMeta('lyx-changes', true));
    return true;
  };
}

const changesOfDoc = new WeakMap<PMNode, ChangeRange[]>();
function cachedChanges(doc: PMNode): ChangeRange[] {
  let all = changesOfDoc.get(doc);
  if (!all) changesOfDoc.set(doc, all = allChanges(doc));
  return all;
}

/**
 * The change run at `pos` (`boundary`: the paragraph break that ends there) together with the runs
 * that touch it and have the same author: one suggestion to accept or reject as a whole — a
 * replacement (the deletion and its insertion), a suggestion across paragraph breaks. Null when
 * no change is there.
 */
export function changeGroupAt(doc: PMNode, pos: number, boundary = false): ChangeRange[] | null {
  const all = cachedChanges(doc);
  const i = all.findIndex(r => boundary ? r.boundary && r.from === pos : !r.boundary && r.from <= pos && pos < r.to);
  if (i < 0) return null;
  let a = i, b = i;
  while (a > 0 && all[a - 1].to === all[a].from && all[a - 1].author === all[i].author) a--;
  while (b + 1 < all.length && all[b + 1].from === all[b].to && all[b + 1].author === all[i].author) b++;
  return all.slice(a, b + 1);
}

/**
 * The suggestions (`changeGroupAt` groups, in document order) that a selection from `from` to `to`
 * touches: what ✓ / ✗ settles for a selection of tracked and untracked text alike.
 */
export function changeGroupsIn(doc: PMNode, from: number, to: number): ChangeRange[][] {
  const groups: ChangeRange[][] = [];
  for (const r of cachedChanges(doc)) {
    if (r.from >= to || r.to <= from) continue;
    if (groups.some(g => g.includes(r))) continue;
    const g = changeGroupAt(doc, r.from, !!r.boundary);
    if (g) groups.push(g);
  }
  return groups;
}

/* ------------------------------------------------ display filter (insertions / deletions) */

/**
 * Purely a *view* filter: which tracked-change types are drawn. Independent of the document
 * itself — insertions and deletions can each be hidden (both, either, or neither), e.g. to read
 * the text as it will look once deletions are accepted while still seeing what was inserted.
 * A hidden run does not vanish without a trace: a small caret stands where it is (like a folded
 * region; hovering it shows the hidden text), and clicking it unfolds just that run — the toolbar
 * switches then act as fold / unfold *all* (flipping one clears the per-run exceptions). Runs side
 * by side, or with only spaces between them, share one caret (and the spaces fold with them).
 */
export interface ChangesFilterState { showInsertions: boolean; showDeletions: boolean; unfolded: ReadonlySet<string> }
export const changesFilterKey = new PluginKey<ChangesFilterState>('lyx-changes-filter');
export type ChangesFilterPatch = Partial<Pick<ChangesFilterState, 'showInsertions' | 'showDeletions'>> & { toggleRun?: string };

/** one edit's identity: stable across position shifts (an agent or user edit shares author+time) */
const runKeyOf = (c: { type: string; author: number; time: number }) => `${c.type}:${c.author}:${c.time}`;

/** `keys`: the runs it stands for, one per line; `runs`: how many separate runs; `text`: what they hide (the start of it) */
const foldMarker = (keys: string, type: string, folded: boolean, runs: number, text: string) => () => {
  const el = document.createElement('span');
  el.className = `ol-change-fold ${type}${folded ? '' : ' open'}`;
  el.setAttribute('data-fold', keys);
  const what = type === 'deleted' ? 'deletion' : type === 'inserted' ? 'insertion' : 'change';
  el.title = folded ? `${runs > 1 ? `${runs} hidden ${what}s` : `A hidden ${what}`}${text ? `: “${text}”` : ''} — click to show` : `Click to hide ${runs > 1 ? 'these changes' : 'this change'} again`;
  el.contentEditable = 'false';
  return el;
};

export function changesFilterPlugin(): Plugin<ChangesFilterState> {
  return new Plugin<ChangesFilterState>({
    key: changesFilterKey,
    state: {
      init: () => ({ showInsertions: true, showDeletions: true, unfolded: new Set<string>() }),
      apply(tr, prev) {
        const meta = tr.getMeta(changesFilterKey) as ChangesFilterPatch | undefined;
        if (!meta) return prev;
        let unfolded: ReadonlySet<string> = prev.unfolded;
        if (meta.toggleRun) {
          // (one caret may stand for several runs: all of them open, or all close again)
          const keys = meta.toggleRun.split('\n').filter(Boolean);
          const next = new Set(unfolded);
          const open = keys.some(k => !next.has(k));
          for (const k of keys) { if (open) next.add(k); else next.delete(k); }
          unfolded = next;
        }
        // the toolbar switches mean fold / unfold ALL: flipping one resets the per-run exceptions
        if (meta.showInsertions !== undefined || meta.showDeletions !== undefined) unfolded = new Set();
        return { showInsertions: meta.showInsertions ?? prev.showInsertions, showDeletions: meta.showDeletions ?? prev.showDeletions, unfolded };
      },
    },
    props: {
      decorations(state) {
        const f = changesFilterKey.getState(state)!;
        if (f.showInsertions && f.showDeletions) return null;
        const decos: Decoration[] = [];
        // the filtered runs one caret stands for: side by side, or with only spaces between them
        type Group = { from: number; end: number; keys: string[]; types: Set<string>; runs: number; lastKey: string; text: string; nodes: { from: number; to: number; text: boolean }[]; gaps: { from: number; to: number }[] };
        let g: Group | null = null;
        let gap: { from: number; to: number; text: string } | null = null;   // spaces right after the group: its own if another hidden run follows
        const flush = () => {
          if (g) {
            const folded = g.keys.some(k => !f.unfolded.has(k));
            const type = g.types.size === 1 ? [...g.types][0] : 'mixed';
            const keys = g.keys.join('\n');
            const text = g.text.trim().replace(/\s+/g, ' ');
            decos.push(Decoration.widget(g.from, foldMarker(keys, type, folded, g.runs, text.length > 60 ? text.slice(0, 60) + '…' : text), { side: -1, key: `fold:${keys}:${folded}` }));
            const cls = folded ? 'lyx-change-hidden' : 'lyx-change-unfolded';
            for (const n of g.nodes) decos.push(n.text ? Decoration.inline(n.from, n.to, { class: cls }) : Decoration.node(n.from, n.to, { class: cls }));
            if (folded) {
              for (const s of g.gaps) decos.push(Decoration.inline(s.from, s.to, { class: 'lyx-change-hidden' }));
              // a word folded away between two spaces leaves one of them: "the ‸ fox", not "the  ‸  fox"
              const $f = state.doc.resolve(g.from), $e = state.doc.resolve(g.end);
              const before = g.from > $f.start() ? state.doc.textBetween(g.from - 1, g.from, '', '\ufffc') : '';
              const after = g.end < $e.end() ? state.doc.textBetween(g.end, g.end + 1, '', '\ufffc') : '';
              if (/^\s$/.test(after) && (before === '' || /^\s$/.test(before))) decos.push(Decoration.inline(g.end, g.end + 1, { class: 'lyx-change-hidden' }));
            }
          }
          g = null; gap = null;
        };
        state.doc.descendants((node, pos) => {
          if (!node.isInline) { flush(); return true; }
          const c = changeOf(node);
          const hit = c && ((c.type === 'inserted' && !f.showInsertions) || (c.type === 'deleted' && !f.showDeletions));
          if (!hit) {
            if (g && !gap && node.isText && pos === g.end && /^\s+$/.test(node.text ?? '')) { gap = { from: pos, to: pos + node.nodeSize, text: node.text! }; return true; }
            flush();
            return true;
          }
          const key = runKeyOf(c);
          const touching = !!g && pos === g.end;
          if (g && (touching || (gap && pos === gap.to))) {
            if (gap) { g.gaps.push(gap); g.text += gap.text; gap = null; }
          } else {
            flush();
            g = { from: pos, end: pos, keys: [], types: new Set(), runs: 0, lastKey: '', text: '', nodes: [], gaps: [] };
          }
          const grp = g!;
          if (!touching || key !== grp.lastKey) grp.runs++;
          if (!grp.keys.includes(key)) grp.keys.push(key);
          grp.types.add(c.type);
          grp.lastKey = key;
          grp.end = pos + node.nodeSize;
          grp.nodes.push({ from: pos, to: grp.end, text: node.isText });
          if (grp.text.length < 80) grp.text += node.isText ? node.text : ' ';
          return node.isText;   // a hidden inset goes as a whole
        });
        flush();
        return decos.length ? DecorationSet.create(state.doc, decos) : null;
      },
      handleDOMEvents: {
        mousedown(view, ev) {
          const fold = (ev.target as HTMLElement).closest?.('.ol-change-fold');
          if (!fold) return false;
          ev.preventDefault();
          setChangesFilter(view, { toggleRun: fold.getAttribute('data-fold') ?? '' });
          return true;
        },
      },
    },
  });
}

export function setChangesFilter(view: EditorView, patch: ChangesFilterPatch): void {
  view.dispatch(view.state.tr.setMeta(changesFilterKey, patch));
}
